import { useState } from "react";
import InputSearch from "components/ui/InputSearch";
import Modal from "components/ui/modals";
import MasterPage, {
  AddButton,
  ClearFiltersButton,
  EditAction,
  DeleteAction,
  FilterCell,
  FilterRow,
  StatusIcon,
} from "components/ui/MasterPage";
import SetHsn from "components/superAdmin/hsn/SetHsn";
import DeleteHsn from "components/superAdmin/hsn/DeleteHsn";
import {
  useAllSuperAdminHsn,
  useAllSuperAdminHsnGroups,
} from "http-hooks/superAdmin/useSuperAdminHsn";

const hsnColumns = [
  { key: "code_type", header: "Type" },
  { key: "hsncode", header: "Code" },
  { key: "hsndes", header: "Description" },
  { key: "taxrate1", header: "Rate 1 (%)" },
  { key: "amt1", header: "Threshold" },
  { key: "taxrate2", header: "Rate 2 (%)" },
  { key: "ag_status", header: "Status", render: (item) => <StatusIcon value={item.ag_status} /> },
  { key: "actions", header: "Actions", isActions: true },
];

const groupColumns = [
  { key: "hsngrpcode", header: "Group Code" },
  { key: "hsngrpdes", header: "Description" },
  { key: "hsndate", header: "Effective Date" },
  { key: "taxrate1", header: "Rate 1 (%)" },
  { key: "amt1", header: "Threshold" },
  { key: "taxrate2", header: "Rate 2 (%)" },
  { key: "ag_status", header: "Status", render: (item) => <StatusIcon value={item.ag_status} /> },
  { key: "actions", header: "Actions", isActions: true },
];

export default function HsnManagement() {
  const [activeTab, setActiveTab] = useState("hsn");
  const [search, setSearch] = useState("");
  const [setItemId, setSetItemId] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const isGroup = activeTab === "group";
  const { data: hsnRecords = [], isLoading: isHsnLoading } = useAllSuperAdminHsn(search);
  const { data: groupRecords = [], isLoading: isGroupLoading } = useAllSuperAdminHsnGroups(search);
  const data = isGroup ? groupRecords : hsnRecords;

  const switchTab = (tab) => {
    setActiveTab(tab);
    setSearch("");
    setSetItemId(null);
    setDeleteItemId(null);
  };

  const title = isGroup ? "HSN Group Management" : "HSN Master Management";
  const description = isGroup
    ? "Maintain two-digit HSN group tax rules and effective dates."
    : "Maintain HSN codes used by company item masters.";

  return (
    <>
      <div className="px-4 pt-6 sm:px-6 lg:px-8">
        <div className="inline-flex rounded-md shadow-sm">
          <button
            type="button"
            onClick={() => switchTab("hsn")}
            className={`rounded-l-md px-4 py-2 text-sm font-semibold ${
              !isGroup
                ? "bg-indigo-600 text-white"
                : "bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            }`}
          >
            HSN Masters
          </button>
          <button
            type="button"
            onClick={() => switchTab("group")}
            className={`rounded-r-md px-4 py-2 text-sm font-semibold ${
              isGroup
                ? "bg-indigo-600 text-white"
                : "bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            }`}
          >
            HSN Groups
          </button>
        </div>
      </div>

      <MasterPage
        title={title}
        description={description}
        headerActions={
          <AddButton onClick={() => setSetItemId("new")}>
            Add {isGroup ? "HSN Group" : "HSN"}
          </AddButton>
        }
        filters={
          <FilterRow>
            <FilterCell>
              <InputSearch
                label="Search"
                placeholder={isGroup ? "Group code or description" : "HSN code or description"}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onSearch={setSearch}
              />
            </FilterCell>
            <ClearFiltersButton onClick={() => setSearch("")} />
          </FilterRow>
        }
        columns={isGroup ? groupColumns : hsnColumns}
        data={data}
        isLoading={isGroup ? isGroupLoading : isHsnLoading}
        rowKey="hid"
        renderActions={(item) => (
          <>
            <EditAction onClick={() => setSetItemId(item.hid)} />
            <DeleteAction onClick={() => setDeleteItemId(item.hid)} />
          </>
        )}
        extraModals={
          <>
            {setItemId && (
              <Modal open setOpen={() => setSetItemId(null)} maxWidth="2xl">
                <SetHsn
                  id={setItemId === "new" ? null : setItemId}
                  type={isGroup ? "group" : "hsn"}
                  onClose={() => setSetItemId(null)}
                />
              </Modal>
            )}
            {deleteItemId && (
              <Modal open setOpen={() => setDeleteItemId(null)}>
                <DeleteHsn
                  id={deleteItemId}
                  type={isGroup ? "group" : "hsn"}
                  onClose={() => setDeleteItemId(null)}
                />
              </Modal>
            )}
          </>
        }
      />
    </>
  );
}
